import {
  adhocOverrideFieldKeys,
  countScopeItemsThatCount,
  isGenerationBlockedForNoScope,
} from '@protoss/shared';

import type { SalesDealQuestionnaireSummary } from '../services/salesApi';
import type { QuestionnaireAnswerMap } from './questionnaire-field-visibility';

/**
 * Deal-page adapter for the "no scope captured" gate (COS-100, docs/plans/72): reads the shared
 * predicate's inputs from the questionnaire summary (AI mode, PRIMARY ad-hoc scope items across
 * every ad-hoc section that COUNT toward the estimate - costed, or overridden in the saved
 * estimate - and the tech-stack answer) so the page disables Generate on exactly the deals the
 * API refuses. `answers` is the live answer map (Generate saves it first), keyed like the rest of
 * the page: templateFieldId when set, else fieldKey. `estimateOverrides` is the saved estimate's
 * `overrides` map (null before the first generation).
 */
export function isDealPageBlockedForNoScope(
  questionnaire: SalesDealQuestionnaireSummary | null,
  categoryKey: string | null | undefined,
  answers: QuestionnaireAnswerMap,
  estimateOverrides: unknown = null
): boolean {
  if (!questionnaire) return false;
  // A section-less template returns no sections at all (not even the ad-hoc one), so scope can
  // neither be seen nor added here - say nothing and let the API stay the authority. Generate is
  // already disabled for such deals by the questionnaire-sections check.
  if (questionnaire.sections.length === 0) return false;
  const fields = questionnaire.sections.flatMap((s) => s.groups.flatMap((g) => g.fields));
  const techStack = fields.find((f) => f.fieldKey === 'tech_stack_systems');
  const techStackAnswer = techStack
    ? (answers[
        techStack.templateFieldId !== null ? String(techStack.templateFieldId) : techStack.fieldKey
      ] ?? techStack.value)
    : null;
  const primaries = fields
    .filter((f) => f.isAdhocPrimary)
    .map((f) => ({ fieldKey: f.fieldKey, scopeStatus: f.scopeMeta?.scopeStatus }));
  return isGenerationBlockedForNoScope({
    aiMode: questionnaire.aiMode,
    categoryKey,
    primaryScopeItemCount: countScopeItemsThatCount(
      primaries,
      adhocOverrideFieldKeys(estimateOverrides)
    ),
    techStackSystemsAnswer: techStackAnswer,
  });
}
